import { useState, useEffect, useMemo, useContext } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { Cell, Column } from 'react-table'
import { HomeId } from '@racepointenergy/swc/scs/types'
import { dealerMonitorStates } from '@racepointenergy/swc/scs/dealer-monitor-states'
import {
  State,
  stateEntityAdapter,
} from '@racepointenergy/swc/scs/features/host-states'
import _isEqual from 'lodash/isEqual'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { CommonSearch, TableLoader, Table } from 'components/Table'
import { homeActiveConfigEnergySelector } from 'features/home/selectors'
import { selectedHomeSelector } from 'features/home/selectors'
import { HomeStateContext } from 'features/home/homeStateContext'
import { FormattedMessage, useIntl } from 'react-intl'
import { messages } from './messages'
import {
  PowerCycleWrapper,
  PowerCycleHeader,
  PowerCycleSearchWrapper,
  TableWrapper,
} from './styles'
import { fetchHomeActiveConfigEnergyThunk } from '../HomeEnergySystem/slice'
import { getSortOptions } from 'components/Table/utils'
import { Circuit, Panel, SmartHardware, Load } from 'features/home/types'
import { StateCell, OutletCell, PowerCycleCell } from './cells'
import PowerCycleBlock from './PowerCycleBlock'
import ConfirmModal from 'components/Modal/ConfirmModal'
import AvailabilityChecker from 'features/home/SysMonGuard/AvailabilityChecker'
import NotAvailableHost from '../HomeStatus/NotAvailableHost'

interface PowerCycleProps
  extends RouteComponentProps<{ rootDomainId: string; homeId: HomeId }> {}

interface ConfirmModalType {
  homeId?: string
  cycle?: boolean
  onConfirm?: () => void
}

function PowerCycle({ match }: PowerCycleProps) {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const [searchValue, setSearchValue] = useState<string>()
  const [entities, setEntities] = useState<{
    [key: string]: State | undefined
  }>({})
  const [panelData, setPanelData] = useState<Panel[]>([])
  const [pduData, setPduData] = useState<SmartHardware[][]>([])
  const [pduThreeData, setPduThreeData] = useState<Load[][]>([])
  const [pduPanels, setPduPanels] = useState<Panel[]>([])
  const [pduThreePanels, setPduThreePanels] = useState<Panel[]>([])
  const [confirmModal, setConfirmModal] = useState<ConfirmModalType>({})
  const { isOnline } = useContext(HomeStateContext)

  const { homeId } = match.params

  const currentHome = useAppSelector(selectedHomeSelector.data)

  const hostStateInitialState = stateEntityAdapter.getInitialState()

  const isHomeActiveConfigEnergyLoaded = useAppSelector(
    homeActiveConfigEnergySelector.isFinished
  )
  const homeActiveConfigEnergy = useAppSelector(
    homeActiveConfigEnergySelector.data
  )

  const { data = hostStateInitialState, isFetching } =
    dealerMonitorStates.useQuery({ id: homeId })

  const accessEnabled = currentHome?.configAccessExpires
    ? currentHome.configAccessExpires > Date.now()
    : false

  const headers = useMemo(
    () => ({
      load: {
        label: formatMessage(messages.headerDevice),
        id: 'Device',
      },
      consuming: {
        label: formatMessage(messages.headerOutlet),
        id: 'Outlet',
      },
      state: {
        label: formatMessage(messages.headerState),
        id: 'State',
      },
      status: {
        label: formatMessage(messages.headerCycle),
        id: 'Cycle',
      },
    }),
    [formatMessage]
  )

  const columns: readonly Column<SmartHardware>[] = useMemo(
    () => [
      {
        id: headers.load.id,
        Header: headers.load.label,
        accessor: (prop) => prop.circuitName,
        percentWidth: '45%',
      },
      {
        id: headers.consuming.id,
        Header: headers.consuming.label,
        accessor: (prop) => prop.olaConfig.SlotNumber,
        Cell: OutletCell,
        percentWidth: '15%',
      },
      {
        id: headers.state.id,
        Header: headers.state.label,
        accessor: (row) => row.status,
        Cell: (cell: Cell<Circuit>) =>
          StateCell(cell, homeId, setConfirmModal, accessEnabled),
        disableSortBy: true,
        percentWidth: '15%',
      },
      {
        id: headers.status.id,
        Header: headers.status.label,
        Cell: (cell: Cell<Circuit>) =>
          PowerCycleCell(cell, homeId, setConfirmModal, accessEnabled),
        disableSortBy: true,
        accessor: (row) => row.circuitName,
        percentWidth: '25%',
      },
    ],
    [headers, homeId, accessEnabled]
  )

  const pduThreeColumns: readonly Column<Load>[] = useMemo(
    () => [
      {
        id: headers.load.id,
        Header: headers.load.label,
        accessor: (prop) => prop.circuitName,
        percentWidth: '45%',
      },
      {
        id: headers.consuming.id,
        Header: headers.consuming.label,
        accessor: (prop) => prop.offset + 1,
        Cell: OutletCell,
        percentWidth: '15%',
      },
      {
        id: headers.state.id,
        Header: headers.state.label,
        accessor: (row) => row.status,
        Cell: (cell: Cell<Circuit>) =>
          StateCell(cell, homeId, setConfirmModal, accessEnabled),
        disableSortBy: true,
        percentWidth: '15%',
      },
      {
        id: headers.status.id,
        Header: headers.status.label,
        Cell: (cell: Cell<Circuit>) =>
          PowerCycleCell(cell, homeId, setConfirmModal, accessEnabled),
        disableSortBy: true,
        accessor: (row) => row.circuitName,
        percentWidth: '25%',
      },
    ],
    [headers, homeId, accessEnabled]
  )
  const sortOptions = useMemo(() => getSortOptions(headers, true), [headers])
  const defaultSortBy = sortOptions[1]
  const [sortBy, setSortBy] = useState<{ [key: string]: any }>({})

  const panelSort = (sort: any, id: string) => {
    const newSort = { ...sortBy, [id]: sort }
    setSortBy(newSort)
  }

  useEffect(() => {
    if (homeId && !isHomeActiveConfigEnergyLoaded) {
      dispatch(fetchHomeActiveConfigEnergyThunk(homeId))
    }
  }, [dispatch, homeId, isHomeActiveConfigEnergyLoaded])

  useEffect(() => {
    if (!_isEqual(data.entities, entities)) {
      setEntities(data.entities)
    }
  }, [data.entities, entities])

  useEffect(() => {
    if (
      homeActiveConfigEnergy?.energyConfig?.panelConfig?.breakerPanels?.length
    ) {
      const e =
        homeActiveConfigEnergy.energyConfig?.panelConfig.breakerPanels.filter(
          (panel) => panel.panelType === 'Savant Power Distribution Unit'
        )

      const sorting: { [key: string]: any } = {}
      e.forEach((p) => (sorting[p.id] = { ...defaultSortBy, desc: false }))
      setSortBy(sorting)
      setPanelData(e)
    }
  }, [
    defaultSortBy,
    homeActiveConfigEnergy?.energyConfig?.panelConfig?.breakerPanels,
    setPanelData,
  ])

  useEffect(() => {
    if (!homeActiveConfigEnergy?.lightingDevices || isFetching) {
      return
    }

    const p = [] as SmartHardware[][]
    const pThree = [] as Load[][]

    const pPanels = [] as Panel[]
    const pThreePanels = [] as Panel[]

    panelData.forEach((panel) => {
      const smartHardware = homeActiveConfigEnergy.lightingDevices?.flatMap(
        (device) => {
          return device.smartHardware.filter(
            (hardware) =>
              hardware.olaConfig.PanelID === panel.id && hardware.isPDU
          )
        }
      )

      let pduThreeHardware = [] as Load[]
      homeActiveConfigEnergy.lightingDevices?.forEach((device) => {
        if (
          device.type === 'PDUENGYM31' &&
          device.loads &&
          panel.mac[0] === device.uid
        ) {
          pduThreeHardware = device.loads
        }
      })

      if (smartHardware?.length) {
        pPanels.push(panel)
      } else if (pduThreeHardware.length) {
        pThreePanels.push(panel)
      }

      const hwWithStatus = smartHardware?.map((hw) => {
        const circuitName = homeActiveConfigEnergy.energyConfig?.circuits?.find(
          (circuit) => circuit.companion?.includes(hw?.hwUID)
        )?.name

        const status = entities[`Energy.Circuit.${circuitName}.Status`]?.value

        if (status) {
          if ((status & 0b100) === 1) {
            return {
              ...hw,
              status: 2,
              circuitName,
              homeId,
              setConfirmModal,
            }
          } else if ((status & 0b1) === 1) {
            return {
              ...hw,
              status: 1,
              circuitName,
              homeId,
              setConfirmModal,
            }
          }

          return {
            ...hw,
            status: 0,
            circuitName,
            homeId,
            setConfirmModal,
          }
        }

        return { ...hw, status: -1, circuitName, homeId, setConfirmModal }
      })

      const pduThreeHwWithStatus = pduThreeHardware?.map((hw) => {
        const circuitName = homeActiveConfigEnergy.energyConfig?.circuits?.find(
          (circuit) =>
            circuit.companion?.includes(hw?.loadID) ||
            circuit.load === hw?.loadID
        )?.name

        const status = entities[`Energy.Circuit.${circuitName}.Status`]?.value

        if (status || status === 0) {
          // forced on
          if ((status & 0b101) === 5) {
            return {
              ...hw,
              status: 4,
              circuitName,
              homeId,
              setConfirmModal,
            }
          }
          // forced off
          else if ((status & 0b1000000100) === 516) {
            return {
              ...hw,
              status: 3,
              circuitName,
              homeId,
              setConfirmModal,
            }
          }
          // on
          else if ((status & 0b1) === 1) {
            return {
              ...hw,
              status: 1,
              circuitName,
              homeId,
              setConfirmModal,
            }
          }
          // off
          return {
            ...hw,
            status: 0,
            circuitName,
            homeId,
            setConfirmModal,
          }
        }

        return {
          ...hw,
          status: -1,
          circuitName,
          homeId,
          setConfirmModal,
        }
      })

      if (hwWithStatus?.length) {
        p.push(hwWithStatus)
      }
      if (pduThreeHwWithStatus?.length) {
        pThree.push(pduThreeHwWithStatus)
      }
    })

    setPduPanels(pPanels)
    setPduThreePanels(pThreePanels)
    setPduData(p)
    setPduThreeData(pThree)
  }, [
    entities,
    panelData,
    homeActiveConfigEnergy?.lightingDevices,
    homeActiveConfigEnergy?.energyConfig?.circuits,
    isFetching,
    homeId,
  ])

  return (
    <PowerCycleWrapper>
      <ConfirmModal
        isOpen={!!confirmModal?.homeId}
        onDiscard={() => setConfirmModal({})}
        title={formatMessage(
          confirmModal?.cycle
            ? messages.confirmPowerCycle
            : messages.confirmToggleHeader
        )}
        discardBtnTitle={<FormattedMessage {...messages.closeButton} />}
        confirmBtnTitle={formatMessage(
          confirmModal?.cycle
            ? messages.powerCycleButton
            : messages.toggleButton
        )}
        onConfirm={() => {
          confirmModal?.onConfirm?.()
          setConfirmModal({})
        }}
        variant="primary"
      >
        {formatMessage(
          confirmModal?.cycle ? messages.powerCycleBody : messages.toggleBody
        )}
      </ConfirmModal>

      {/* <MobileSortBy
        headers={headers}
        sortOptions={sortOptions}
        sortBy={sortBy}
        setSortBy={setSortBy}
      /> */}
      <div>
        {!isHomeActiveConfigEnergyLoaded && isOnline && <TableLoader />}
        {!isOnline && (
          <AvailabilityChecker homeId={homeId}>
            {({ errors }) => {
              return (
                <>
                  <NotAvailableHost
                    title={formatMessage(messages.unavailableTitle)}
                    captions={errors}
                  />
                </>
              )
            }}
          </AvailabilityChecker>
        )}
        {isHomeActiveConfigEnergyLoaded && isOnline && (
          <>
            <PowerCycleSearchWrapper>
              <CommonSearch
                setSearchValue={setSearchValue}
                placeholder={formatMessage(messages.searchPlaceholder)}
              />
            </PowerCycleSearchWrapper>
            <TableWrapper>
              {pduData.map((pdu, index) => (
                <div key={pduPanels[index].id}>
                  <PowerCycleHeader>
                    {pduPanels[index]?.panelName}
                  </PowerCycleHeader>

                  <Table
                    columns={columns}
                    data={pdu}
                    defaultSortBy={defaultSortBy}
                    sortBy={sortBy[pduPanels[index].id]}
                    setSortBy={(sort) => panelSort(sort, pduPanels[index].id)}
                    searchValue={searchValue}
                    mobileViewComponent={PowerCycleBlock}
                  />
                </div>
              ))}
              {pduThreeData.map((pdu, index) => (
                <div key={pduThreePanels[index].id}>
                  <PowerCycleHeader>
                    {pduThreePanels[index]?.panelName}
                  </PowerCycleHeader>

                  <Table
                    columns={pduThreeColumns}
                    data={pdu}
                    defaultSortBy={defaultSortBy}
                    sortBy={sortBy[pduThreePanels[index].id]}
                    setSortBy={(sort) =>
                      panelSort(sort, pduThreePanels[index].id)
                    }
                    searchValue={searchValue}
                    mobileViewComponent={PowerCycleBlock}
                  />
                </div>
              ))}
            </TableWrapper>
          </>
        )}
      </div>
    </PowerCycleWrapper>
  )
}

export default PowerCycle
